import { createTrackerForCategory } from '@gonfalon/analytics/createTrackerForCategory';
// TODO: Repoint in phase 6 of RO-2780 (LegacyMeasuredRolloutState is a local union, not an openapi schema; moves with the types.ts disposition)
import type { LegacyMeasuredRolloutState } from '@gonfalon/measured-rollouts/types';
import type { MeasuredRolloutStatusKind } from '@gonfalon/openapi/types';

const trackMeasuredRolloutsEvent = createTrackerForCategory('Guarded Rollouts');

const trackAddMetricsClicked = () => trackMeasuredRolloutsEvent('Add Metrics Clicked');
const trackMetricsClicked = () => trackMeasuredRolloutsEvent('Metrics Clicked');
const trackEditMetricsClicked = () => trackMeasuredRolloutsEvent('Edit Metrics Clicked');

const trackRemoveAllMetricsClicked = ({ previousMetrics }: { previousMetrics: string[] }) =>
  trackMeasuredRolloutsEvent('Remove All Metrics Clicked', {
    previousMetrics,
  });

const trackMetricsSaveClicked = ({ metrics, previousMetrics }: { metrics: string[]; previousMetrics?: string[] }) =>
  trackMeasuredRolloutsEvent('Metrics Save Clicked', {
    metrics,
    previousMetrics,
  });

const trackMetricsCancelClicked = () => trackMeasuredRolloutsEvent('Metrics Cancel Clicked');
const trackRandomizationUnitSelected = ({ randomizationUnit }: { randomizationUnit: string }) =>
  trackMeasuredRolloutsEvent('Randomization Unit Selected', { randomizationUnit });
const trackMetricsSelected = ({ metrics }: { metrics: string[] }) =>
  trackMeasuredRolloutsEvent('Metrics Selected', { metrics });
const trackReleaseGuardianAvailableChipShown = () =>
  trackMeasuredRolloutsEvent('Release Guardian Available Chip Shown');
const trackReleaseGuardianAddMetricsSuggestionShown = () =>
  trackMeasuredRolloutsEvent('Release Guardian Add Metrics Suggestion Shown');
const trackReleaseStrategyShown = () => trackMeasuredRolloutsEvent('Release Strategy Shown');
const trackReleaseStrategyClosed = () => trackMeasuredRolloutsEvent('Release Strategy Closed');
const trackReleaseStrategySkipped = () => trackMeasuredRolloutsEvent('Release Strategy Skipped');
const trackReleaseStrategyRuleSelected = ({ rule }: { rule: string }) =>
  trackMeasuredRolloutsEvent('Release Strategy Rule Selected', { rule });
const trackReleaseStrategyDurationSelected = ({ duration }: { duration: string }) =>
  trackMeasuredRolloutsEvent('Release Strategy Duration Selected', { duration });
const trackReleaseStrategyRegressionBehaviorSelected = ({ regressionBehavior }: { regressionBehavior: string }) =>
  trackMeasuredRolloutsEvent('Release Strategy Regression Behavior Selected', { regressionBehavior });
const trackReleaseStrategyCustomizeClicked = () => trackMeasuredRolloutsEvent('Release Strategy Customize Clicked');
const trackReleaseStrategyDismissCustomClicked = () =>
  trackMeasuredRolloutsEvent('Release Strategy Dismiss Custom Clicked');
const trackReleaseStrategyNextClicked = ({
  trafficAllocation,
  monitoringDuration,
  alertAction,
  randomizationUnit,
}: {
  trafficAllocation: number;
  monitoringDuration: { value: number; unit: string };
  alertAction: string;
  randomizationUnit?: string;
}) =>
  trackMeasuredRolloutsEvent('Release Strategy Next Clicked', {
    trafficAllocation,
    monitoringDuration,
    randomizationUnit,
    alertAction,
  });
const trackReleaseStrategyBackClicked = () => trackMeasuredRolloutsEvent('Release Strategy Back Clicked');

type Page = 'targeting' | 'monitoring';
const trackStatusBarViewInsightsClicked = ({
  page,
  state,
}: {
  page: Page;
  state: LegacyMeasuredRolloutState | MeasuredRolloutStatusKind;
}) => trackMeasuredRolloutsEvent('Status Bar View Insights Clicked', { page, state });
const trackStatusBarRollBackRuleClicked = ({
  state,
}: {
  state: LegacyMeasuredRolloutState | MeasuredRolloutStatusKind;
}) => trackMeasuredRolloutsEvent('Status Bar Roll Back Rule Clicked', { state });
const trackStatusBarViewResultsClicked = ({
  page,
  state,
}: {
  page: Page;
  state: LegacyMeasuredRolloutState | MeasuredRolloutStatusKind;
}) => trackMeasuredRolloutsEvent('Status Bar View Results Clicked', { page, state });
const trackRuleSummaryShown = ({ state }: { state: LegacyMeasuredRolloutState | MeasuredRolloutStatusKind }) =>
  trackMeasuredRolloutsEvent('Rule Summary Shown', { state });
const trackRuleSummaryViewInsightsClicked = ({
  state,
}: {
  state: LegacyMeasuredRolloutState | MeasuredRolloutStatusKind;
}) => trackMeasuredRolloutsEvent('Rule Summary View Insights Clicked', { state });
const trackRuleSummaryStopMonitoringClicked = ({
  state,
}: {
  state: LegacyMeasuredRolloutState | MeasuredRolloutStatusKind;
}) => trackMeasuredRolloutsEvent('Rule Summary Stop Monitoring Clicked', { state });
const trackRuleSummaryViewResultsClicked = ({
  state,
}: {
  state: LegacyMeasuredRolloutState | MeasuredRolloutStatusKind;
}) => trackMeasuredRolloutsEvent('Rule Summary View Results Clicked', { state });
const trackTargetingSaveButtonClickedWhenPromptedForMonitoring = () => {
  trackMeasuredRolloutsEvent('Targeting Save Button Clicked When Prompted For Monitoring');
};

const trackGuardedRolloutQuickActionClicked = ({ isTrial }: { isTrial: boolean }) =>
  trackMeasuredRolloutsEvent('Guarded Rollout Quick Actions Clicked', { isTrial });
const trackGuardedRolloutCustomThresholdsClicked = () =>
  trackMeasuredRolloutsEvent('Guarded Rollout Custom Thresholds Clicked');
const trackGuardedRolloutDurationSelected = ({ duration }: { duration: string }) =>
  trackMeasuredRolloutsEvent('Guarded Rollout Duration Selected', { duration });
const trackGuardedRolloutMonitoringTabClicked = () =>
  trackMeasuredRolloutsEvent('Guarded Rollout Monitoring Tab Clicked');
const trackGuardedRolloutStarted = () => trackMeasuredRolloutsEvent('Guarded Rollout Started');
const trackGuardedRolloutStoppedRolledForward = () =>
  trackMeasuredRolloutsEvent('Guarded Rollout Stopped and Rolled Forward');
const trackGuardedRolloutStoppedRolledBack = () =>
  trackMeasuredRolloutsEvent('Guarded Rollout Stopped and Rolled Back');
const trackGuardedRolloutOnRuleClicked = () => trackMeasuredRolloutsEvent('Guarded Rollout On Rule Clicked');
const trackGuardedRolloutSelectedFromDropdown = () =>
  trackMeasuredRolloutsEvent('Guarded Rollout Selected From Dropdown');
const trackGuardianPagerDutyConfigured = () => trackMeasuredRolloutsEvent('Guardian PagerDuty Configured');
const trackGuardedRolloutsRegressionDismissed = () =>
  trackMeasuredRolloutsEvent('Guarded Rollouts Regression Dismissed');
const trackGuardedRolloutViewed = (attributes?: Record<string, unknown>) =>
  trackMeasuredRolloutsEvent('Guarded Rollout Viewed', attributes);

const trackRequestTrialButtonClicked = () =>
  trackMeasuredRolloutsEvent('Guarded Rollout Upsell Request Trial Button Clicked');
const trackSandboxButtonClicked = ({ cta, isTrial }: { cta: string; isTrial: boolean }) =>
  trackMeasuredRolloutsEvent('Guarded Rollout Upsell Sandbox Demo Button Clicked', { cta, isTrial });
const trackReadBlogPostButtonClicked = () =>
  trackMeasuredRolloutsEvent('Guarded Rollout Upsell Read Blog Post Button Clicked');
const trackEmptyStateLearnMoreButtonClicked = () =>
  trackMeasuredRolloutsEvent('Guarded Rollout Empty State Learn More Button Clicked', { location: 'Empty State' });
const trackGuardedRolloutUpsellViewed = ({ isTrial }: { isTrial: boolean }) =>
  trackMeasuredRolloutsEvent('Guarded Rollout Upsell Viewed', { isTrial });
const trackGuardedRolloutPageViewed = () => trackMeasuredRolloutsEvent('Guarded Rollout Page Viewed');
const trackGuardedRolloutFlagListViewed = () => trackMeasuredRolloutsEvent('Guarded Rollout Flag List Viewed');
const trackGuardedRolloutConfigAbandoned = () => trackMeasuredRolloutsEvent('Guarded Rollout Config Abandoned');

// This can probably be moved to a different file later - entitlements package?
const trackUsageLimitTrialEvent = createTrackerForCategory('Usage Limit Trials');

const trackUsageAlertViewed = ({ component, header, status }: { component: string; header: string; status: string }) =>
  trackUsageLimitTrialEvent('Usage Alert Viewed', {
    component,
    header,
    status,
  });
const trackUsageAlertDismissed = ({
  component,
  header,
  status,
}: {
  component: string;
  header: string;
  status: string;
}) =>
  trackUsageLimitTrialEvent('Usage Alert Dismissed', {
    component,
    header,
    status,
  });

export {
  trackGuardedRolloutQuickActionClicked,
  trackGuardedRolloutCustomThresholdsClicked,
  trackGuardedRolloutDurationSelected,
  trackGuardedRolloutMonitoringTabClicked,
  trackGuardedRolloutStarted,
  trackGuardedRolloutStoppedRolledForward,
  trackGuardedRolloutStoppedRolledBack,
  trackGuardedRolloutOnRuleClicked,
  trackGuardedRolloutSelectedFromDropdown,
  trackGuardianPagerDutyConfigured,
  trackGuardedRolloutsRegressionDismissed,
  trackAddMetricsClicked,
  trackMetricsClicked,
  trackEditMetricsClicked,
  trackRemoveAllMetricsClicked,
  trackMetricsSaveClicked,
  trackMetricsCancelClicked,
  trackRandomizationUnitSelected,
  trackMetricsSelected,
  trackReleaseGuardianAvailableChipShown,
  trackReleaseGuardianAddMetricsSuggestionShown,
  trackReleaseStrategyShown,
  trackReleaseStrategyClosed,
  trackReleaseStrategySkipped,
  trackReleaseStrategyRuleSelected,
  trackReleaseStrategyDurationSelected,
  trackReleaseStrategyCustomizeClicked,
  trackReleaseStrategyDismissCustomClicked,
  trackReleaseStrategyRegressionBehaviorSelected,
  trackReleaseStrategyNextClicked,
  trackReleaseStrategyBackClicked,
  trackStatusBarViewInsightsClicked,
  trackStatusBarRollBackRuleClicked,
  trackStatusBarViewResultsClicked,
  trackRuleSummaryShown,
  trackRuleSummaryViewInsightsClicked,
  trackRuleSummaryStopMonitoringClicked,
  trackRuleSummaryViewResultsClicked,
  trackTargetingSaveButtonClickedWhenPromptedForMonitoring,
  trackGuardedRolloutViewed,
  trackGuardedRolloutUpsellViewed,
  trackGuardedRolloutPageViewed,
  trackSandboxButtonClicked,
  trackGuardedRolloutFlagListViewed,
  trackGuardedRolloutConfigAbandoned,
  trackRequestTrialButtonClicked,
  trackReadBlogPostButtonClicked,
  trackEmptyStateLearnMoreButtonClicked,
  trackUsageAlertViewed,
  trackUsageAlertDismissed,
};
